/**
 * Tile-growth preference: how a preview grid spends width once it holds `preferredCols`
 * preferred-size tiles (see hooks/adaptiveGrid.ts for what each value does to the layout).
 *
 *   'fixed'    — DEFAULT. Keep adding columns at exactly the preferred tile size; the leftover
 *                is trailing slack (the tracks start-align). A wider window shows MORE previews,
 *                same size.
 *   'resizing' — Hold the column count and let the tiles grow, unbounded. A wider window shows
 *                the same previews, BIGGER.
 *
 * NATIVE-AUTHORITATIVE, structurally a copy of theme/appearance.ts — read that module's header
 * for the full rationale; the short version:
 *   - EMBEDDED: Unity owns the value. It appends ?tileGrowth= on every webview load (boot), ships
 *     the row in the setPreferences catalog (applyTileGrowthFromPreferences), and receives every
 *     change through the ordinary setPreference verb.
 *   - STANDALONE / online review: no bridge, so localStorage is the sole store.
 * localStorage is written in BOTH modes but is only ever a MIRROR: the board webview's teardown
 * calls UniWebView CleanCache(), which drops WKWebsiteDataTypeLocalStorage app-wide, so a value
 * that lives only there is one project-exit away from being lost.
 *
 * Two deliberate differences from appearance.ts:
 *   - No route gate. This changes layout inside the grids themselves, not <html> tokens, so
 *     there is nothing to leak into an unaudited route.
 *   - No index.html pre-paint duplication. A wrong first frame here is a reflow, not a colour
 *     flash, and the pre-paint script's hand-copied param/key/route list is a maintenance
 *     surface worth not doubling.
 */
import type { TileGrowthMode } from '@/hooks/adaptiveGrid';

export type { TileGrowthMode };

/** localStorage mirror key. */
export const TILE_GROWTH_STORAGE_KEY = 'timeline-tile-growth';

/** Query param Unity appends to every embedded webview load — the boot half of native authority
 *  (the catalog cannot serve boot: it is only pushed on requestPreferences). */
export const TILE_GROWTH_PARAM = 'tileGrowth';

/** Matches SystemPreferenceTileGrowthType's default on the native side. */
const DEFAULT_TILE_GROWTH: TileGrowthMode = 'fixed';

let currentTileGrowth: TileGrowthMode = DEFAULT_TILE_GROWTH;
const listeners = new Set<() => void>();

/** A recognised mode, or null for "no answer" — an unset param, a value from a Unity build that
 *  predates this row, or junk. Null NEVER means the default: callers keep what they have, so an
 *  older native side can't silently reset the user's choice. */
function coerceTileGrowth(value: unknown): TileGrowthMode | null {
  return value === 'fixed' || value === 'resizing' ? value : null;
}

/** Best-effort re-seed of the localStorage mirror from live state. */
function writeMirror(mode: TileGrowthMode): void {
  try {
    localStorage.setItem(TILE_GROWTH_STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode, post-CleanCache) — the in-session value still applies */
  }
}

function getStoredTileGrowth(): TileGrowthMode | null {
  try {
    return coerceTileGrowth(localStorage.getItem(TILE_GROWTH_STORAGE_KEY));
  } catch {
    return null;
  }
}

function readParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/**
 * Apply the tileGrowth row of Unity's setPreferences catalog — the authoritative value, pushed
 * when the Preferences panel opens and re-pushed after every setPreference write.
 *
 * Unrecognised input is IGNORED rather than defaulted (see coerceTileGrowth): a catalog from a
 * Unity build without this row leaves the preference untouched, which is what keeps the web
 * bundle safe to ship ahead of the native side. The mirror is re-seeded even when nothing
 * changed — that repair is the whole point after a CleanCache() wipe — but subscribers are only
 * notified on an actual change, so the catalog re-push that follows our own setPreference is a
 * no-op.
 */
export function applyTileGrowthFromPreferences(raw: unknown): void {
  const next = coerceTileGrowth(raw) ?? currentTileGrowth;
  writeMirror(next);
  if (next === currentTileGrowth) return;
  currentTileGrowth = next;
  listeners.forEach((fn) => fn());
}

/** Current preference for UI + the grid hooks. */
export function getTileGrowth(): TileGrowthMode {
  return currentTileGrowth;
}

/** Subscribe to changes (useSyncExternalStore-shaped). */
export function subscribeTileGrowth(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** User-facing setter: persists the mirror and notifies subscribers. The authoritative write
 *  (setPreference) is the caller's job — see PreferencesModal's dual-layer comment. */
export function setTileGrowth(mode: TileGrowthMode): void {
  if (mode !== 'fixed' && mode !== 'resizing') return;
  currentTileGrowth = mode;
  writeMirror(mode);
  listeners.forEach((fn) => fn());
}

/**
 * Re-read the mirror and reconcile. This app runs TWO long-lived same-origin documents (the
 * kept-alive Projects webview is parked while the board runs, and restored WITHOUT a reload), so
 * a document's boot-time cache goes stale when the OTHER document changes the preference. Wired
 * to the cross-document 'storage' event, to visibility/focus recovery, and to useTileGrowth()
 * mount (the Preferences modal mounts fresh on every open).
 *
 * A MISSING key is a WIPE, not a reset to defaults: Unity's board teardown calls CleanCache(),
 * which clears the whole store. Keep the live value and re-seed from it.
 */
export function refreshTileGrowthFromStorage(): void {
  const stored = getStoredTileGrowth();
  if (stored === null) {
    // Nothing to preserve at the default — a fresh boot resolves to exactly this, and skipping
    // the write keeps standalone/online review byte-identical for users who never touched the row.
    if (currentTileGrowth !== DEFAULT_TILE_GROWTH) writeMirror(currentTileGrowth);
    return;
  }
  if (stored === currentTileGrowth) return;
  currentTileGrowth = stored;
  listeners.forEach((fn) => fn());
}

let reconcileListenersAttached = false;

/** Boot entry point — called once from main.tsx, right after initAppearance() (tests call it
 *  repeatedly; the reconcile listeners attach only once). */
export function initTileGrowth(): void {
  // URL param FIRST: in embedded mode Unity appends it on every load and it is the only
  // authoritative value available at boot (the catalog is pull-only). Then the localStorage
  // mirror, then the default.
  const paramMode = coerceTileGrowth(readParam(TILE_GROWTH_PARAM));
  currentTileGrowth = paramMode ?? getStoredTileGrowth() ?? DEFAULT_TILE_GROWTH;
  // Re-seed the mirror from whatever native just told us, so the OTHER same-origin document
  // (the kept-alive Projects webview) agrees with Unity on its next reconcile.
  if (paramMode !== null) writeMirror(currentTileGrowth);
  if (!reconcileListenersAttached) {
    try {
      window.addEventListener('storage', (e) => {
        if (e.key === null || e.key === TILE_GROWTH_STORAGE_KEY) refreshTileGrowthFromStorage();
      });
      // The parked Projects webview is restored in place (no reload, no remount), and
      // WKWebView's cross-document 'storage' delivery is unreliable — re-reading on the way back
      // in is what makes a value picked on the board show up here. Both events: an embedded
      // WKWebView can regain focus without a visibilitychange.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refreshTileGrowthFromStorage();
      });
      window.addEventListener('focus', () => refreshTileGrowthFromStorage());
      reconcileListenersAttached = true;
    } catch {
      /* non-browser environment */
    }
  }
}
